import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "../hooks/useTranslation";

/*
 * TextareaWithCounter
 * A controlled textarea with a character counter and a clear button.
 *
 * Props:
 *   id          {string}   - textarea element id
 *   name        {string}   - field name
 *   value       {string}   - controlled value
 *   onChange    {function} - called with a synthetic event: onChange({ target: { name, value } })
 *   maxLength        {number}   - character cap (input is blocked beyond this)
 *   rows             {number}   - textarea rows (default: 4)
 *   clearLabel       {string}   - text for the clear button (default: "Delete")
 *   errorMessage     {string}   - validation error message to display below the counter
 *
 * ─── Usage: plain useState (no form library) ───────────────────────────────
 *
 *   const [giftText, setGiftText] = useState("");
 *
 *   <TextareaWithCounter
 *     id="GiftFreeTextInput"
 *     name="GiftFreeTextInput"
 *     value={giftText}
 *     onChange={(e) => setGiftText(e.target.value)}
 *     maxLength={200}
 *     clearLabel={t("Delete")}
 *   />
 *
 * ─── Usage: react-hook-form with register + watch ──────────────────────────
 *
 *   import { useForm } from "react-hook-form";
 *
 *   const { register, watch, setValue, formState: { errors } } = useForm();
 *   const giftText = watch("GiftFreeTextInput", "");
 *
 *   <TextareaWithCounter
 *     {...register("GiftFreeTextInput")}
 *     value={giftText}
 *     onChange={(e) => setValue("GiftFreeTextInput", e.target.value, { shouldValidate: true })}
 *     maxLength={giftTextAreaMaxLength}
 *     clearLabel={t("Delete")}
 *     errorMessage={errors.GiftFreeTextInput?.message}
 *   />
 */

const TextareaWithCounter = React.forwardRef(({
  id,
  name,
  value = "",
  onChange,
  maxLength,
  rows = 4,
  clearLabel = "Delete",
  errorMessage,
  ...rest
}, ref) => {
  const ZERO_WIDTH_SPACE = String.fromCharCode(8203);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedAttempts, setBlockedAttempts] = useState(0);
  const { t } = useTranslation();

  const atLimitMessage = t("TextAreaAtLimitMsg");
  const overLimitMessage = t("TextAreaOverLimitMsg");
  const isFull = value.length === Number(maxLength);

  const handleChange = (e) => {
    const val = e.target.value;
    if (val.length > maxLength) {
      setIsBlocked(true);
      setBlockedAttempts((n) => n + 1);
      return;
    }
    setIsBlocked(false);
    onChange(e);
  };

  const handleClear = () => {
    setIsBlocked(false);
    onChange({ target: { name, value: "" } });
  };

  const displayError = errorMessage || (isBlocked ? overLimitMessage : "");
  const displayInfo = !displayError && isFull ? atLimitMessage : "";

  return (
    <>
      <Form.Group className="textarea-with-counter">
        <Form.Control as="textarea"
          ref={ref}
          id={id}
          name={name}
          className="text-area"
          value={value}
          onChange={handleChange}
          rows={rows}
          aria-describedby={`${id}-error ${id}-hint`}
          {...rest}
        />
      </Form.Group>

      <div className="words-counter botom-inputs">
        <span>
          <span className="error" id={`${id}-hint`}>{displayInfo}</span>
          <span className="error" id={`${id}-error`}>
            {displayError}{isBlocked && blockedAttempts % 2 === 0 ? ZERO_WIDTH_SPACE : ""}
          </span>
        </span>
        <span>
          <span role="button" aria-label={clearLabel} className="delete" onClick={handleClear}>
            {clearLabel}
          </span>
          <span>{value.length}/{maxLength}</span>
        </span>
      </div>


    </>
  );
});

TextareaWithCounter.displayName = "TextareaWithCounter";

export default TextareaWithCounter;
